import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ContentWrapper from "../../components/contentwrapper/ContentWrapper";
import ReportNavBar from "../../components/ReportNavBar";
import withAuth from "../../utils/withAuth";
import { fetchCallAllDataByIDAPI } from "../../utils/api";

const HOURS = Array.from({ length: 24 }, (_, i) => i + 1);

// ✅ Skeleton helpers
const SkeletonLine = ({ w = "100%", h = 14, mb = 8 }) => (
    <div className="sk" style={{ width: w, height: h, marginBottom: mb }} />
);

const HourlyTableSkeleton = ({ rows = 6 }) => (
    <>
        {Array.from({ length: rows }).map((_, i) => (
            <tr key={`hsk-${i}`}>
                <td>
                    <SkeletonLine w="55px" h={12} mb={0} />
                </td>
                <td style={{ textAlign: "right" }}>
                    <SkeletonLine w="70px" h={12} mb={0} />
                </td>
                <td style={{ textAlign: "right" }}>
                    <SkeletonLine w="70px" h={12} mb={0} />
                </td>
                <td style={{ textAlign: "right" }}>
                    <SkeletonLine w="90px" h={12} mb={0} />
                </td>
                <td style={{ textAlign: "right" }}>
                    <SkeletonLine w="80px" h={12} mb={0} />
                </td>
                <td style={{ textAlign: "right" }}>
                    <SkeletonLine w="55px" h={12} mb={0} />
                </td>
            </tr>
        ))}
    </>
);

const perCallCost = (spend, calls) => {
    const c = Number(calls || 0);
    const s = Number(spend || 0);
    if (c <= 0) return 0;
    return s / c;
};

const SkillCardSkeleton = () => (
    <div className="col-md-6 col-lg-4 mb-3">
        <div className="card shadow-sm">
            <div className="card-body p-2">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <SkeletonLine w="55%" h={16} mb={0} />
                    <SkeletonLine w="35%" h={12} mb={0} />
                </div>

                <div
                    className="table-responsive"
                    style={{ maxHeight: 260, overflowY: "auto" }}
                >
                    <table className="table table-sm table-bordered mb-0 mini-table">
                        <thead>
                            <tr>
                                <th style={{ width: 60, textAlign: "right" }}>Hour</th>
                                <th style={{ width: 80, textAlign: "right" }}>Calls</th>
                                <th style={{ width: 80, textAlign: "right" }}>Orders</th>
                                <th style={{ width: 110, textAlign: "right" }}>Spend</th>
                                <th style={{ width: 110, textAlign: "right" }}>Per Call</th>
                                <th style={{ width: 70, textAlign: "right" }}>Perf</th>
                            </tr>
                        </thead>

                        <tbody>
                            <HourlyTableSkeleton rows={6} />
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
);

const HourlyPPCYesterdayReport = () => {
    const [rows, setRows] = useState([]);
    const [hours, setHours] = useState([]);
    const [summary, setSummary] = useState(null);
    const [meta, setMeta] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showSkills, setShowSkills] = useState(true);

    // ✅ Range states
    const [startHour, setStartHour] = useState(1);
    const [endHour, setEndHour] = useState(1);

    // ✅ Applied range (actual active filter)
    const [appliedRange, setAppliedRange] = useState({ start: 1, end: 1 });

    // ✅ Skill filter states
    const [skillOpen, setSkillOpen] = useState(false);
    const [skillSearch, setSkillSearch] = useState("");
    const [selectedSkills, setSelectedSkills] = useState(new Set()); // working selection
    const [appliedSkills, setAppliedSkills] = useState(new Set()); // applied selection
    const skillRef = useRef(null);

    const navigate = useNavigate();
    const ppcid = localStorage.getItem("ppcdata");

    // ✅ Default = current hour only
    useEffect(() => {
        const now = new Date();
        const h24 = now.getHours(); // 0-23
        const currentHour = h24 === 0 ? 24 : h24; // 1-24 feel

        setStartHour(currentHour);
        setEndHour(currentHour);
        setAppliedRange({ start: currentHour, end: currentHour });
    }, []);

    useEffect(() => {
        if (!ppcid) return navigate("/");
        navigate("/hourly-yesterday-report");
    }, [ppcid, navigate]);

    // ✅ Close skill dropdown on outside click
    useEffect(() => {
        const onDoc = (e) => {
            if (!skillRef.current) return;
            if (!skillRef.current.contains(e.target)) setSkillOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    // ✅ Load ONLY when appliedRange changes
    useEffect(() => {
        if (!ppcid) return;
        if (!appliedRange?.start || !appliedRange?.end) return;
        load(ppcid, appliedRange.start, appliedRange.end);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ppcid, appliedRange]);

    const load = async (id, sh, eh) => {
        setIsLoading(true);
        try {
            const res = await fetchCallAllDataByIDAPI(
                `ppc-team-get-yesterday-live-call-hour-wise-with-proccess-name-by-id/${id}?start_hour=${sh}&end_hour=${eh}`
            );

            if (res?.data?.status === 1) {
                const p = res?.data?.process || {};
                const newRows = Array.isArray(p?.rows) ? p.rows : [];

                setRows(newRows);
                setHours(Array.isArray(p?.hours) ? p.hours : []);
                setSummary(p?.summary || null);
                setMeta(p?.meta || { start_hour: sh, end_hour: eh });

                // ✅ Default skills = ALL (first load or if API returns new skills)
                const allSkillNames = newRows
                    .map((x) => String(x?.skillname || "").trim())
                    .filter(Boolean);

                const allSet = new Set(allSkillNames);

                setSelectedSkills((prev) => (prev && prev.size ? prev : allSet));
                setAppliedSkills((prev) => (prev && prev.size ? prev : allSet));
            } else {
                toast.error(res?.data?.message || "Failed to load hourly report");
                setRows([]);
                setHours([]);
                setSummary(null);
                setMeta(null);
            }
        } catch (e) {
            console.error(e);
            toast.error("Network/CORS error while loading hourly report");
            setRows([]);
            setHours([]);
            setSummary(null);
            setMeta(null);
        } finally {
            setIsLoading(false);
        }
    };

    // ✅ Apply range button
    const onApply = () => {
        const sh = Number(startHour);
        const eh = Number(endHour);

        if (!sh || !eh) return toast.error("Please select both From & To hour");
        if (sh > eh) return toast.error("From hour cannot be greater than To hour");

        setAppliedRange({ start: sh, end: eh });
    };

    // ✅ Skill options
    const skillOptions = useMemo(() => {
        const names = (rows || [])
            .map((x) => String(x?.skillname || "").trim())
            .filter(Boolean);

        return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
    }, [rows]);

    const filteredSkillOptions = useMemo(() => {
        const q = skillSearch.trim().toLowerCase();
        if (!q) return skillOptions;
        return skillOptions.filter((s) => s.toLowerCase().includes(q));
    }, [skillOptions, skillSearch]);

    const toggleSkill = (name) => {
        setSelectedSkills((prev) => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name);
            else next.add(name);
            return next;
        });
    };

    const onSelectAllSkills = () => setSelectedSkills(new Set(skillOptions));
    const onClearSkills = () => setSelectedSkills(new Set());

    const onApplySkills = () => {
        if (!selectedSkills.size) {
            const all = new Set(skillOptions);
            setSelectedSkills(all);
            setAppliedSkills(all);
            toast.info("No skill selected, showing All Skills");
            setSkillOpen(false);
            return;
        }

        setAppliedSkills(new Set(selectedSkills));
        setSkillOpen(false);
    };

    const onResetSkillsToAll = () => {
        const all = new Set(skillOptions);
        setSelectedSkills(all);
        setAppliedSkills(all);
        setSkillSearch("");
        setSkillOpen(false);
    };

    const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
    const fmtMoney = (n) =>
        Number(n || 0).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

    const top = useMemo(() => {
        const tCalls = summary?.total_calls ?? 0;
        const tOrders = summary?.total_orders ?? 0;
        const tSpend = summary?.total_spend ?? 0;
        const tPerf = summary?.perf ?? (tCalls ? (tOrders / tCalls) * 100 : 0);
        return { tCalls, tOrders, tSpend, tPerf };
    }, [summary]);

    const sortedRows = useMemo(() => {
        return [...(rows || [])].sort(
            (a, b) => (Number(b.total_calls) || 0) - (Number(a.total_calls) || 0)
        );
    }, [rows]);

    // ✅ Apply skills filter to cards
    const visibleSkillRows = useMemo(() => {
        if (!appliedSkills || appliedSkills.size === 0) return sortedRows;
        return sortedRows.filter((r) =>
            appliedSkills.has(String(r?.skillname || "").trim())
        );
    }, [sortedRows, appliedSkills]);

    const rangeTitle = useMemo(() => {
        const sh = meta?.start_hour ?? appliedRange.start;
        const eh = meta?.end_hour ?? appliedRange.end;
        if (!sh || !eh) return "Yesterday Hourly";
        return `Yesterday Hourly (${sh}–${eh})`;
    }, [meta, appliedRange]);

    const isPendingChange = useMemo(() => {
        return (
            Number(startHour) !== Number(appliedRange.start) ||
            Number(endHour) !== Number(appliedRange.end)
        );
    }, [startHour, endHour, appliedRange]);

    const skillButtonText = useMemo(() => {
        if (!appliedSkills || appliedSkills.size === 0) return "All";
        if (appliedSkills.size === skillOptions.length) return "All";
        if (appliedSkills.size === 1) return Array.from(appliedSkills)[0];
        return `${appliedSkills.size} Selected`;
    }, [appliedSkills, skillOptions]);

    return (
        <div className="misDashboard">
            <Helmet>
                <title>SATKARTAR::HOURLY REPORT</title>
            </Helmet>

            <style>{`
        .mini-table{ table-layout: fixed; width:100%; }
        .mini-table th,.mini-table td{font-size:12px; padding:8px 10px; white-space:nowrap;}
        .filterRow{display:flex;gap:8px;align-items:end;flex-wrap:wrap;}
        .filterBox{display:flex;flex-direction:column;gap:4px;}
        .filterBox label{font-weight:600;font-size:12px;margin:0;}
        .filterBox select{min-width:120px;}
        .pending{font-size:12px;color:#ff7a00;font-weight:600;}

        /* ✅ skeleton shimmer */
        .sk{position:relative; overflow:hidden; background:#eee; border-radius:6px;}
        .sk:after{
          content:""; position:absolute; inset:0; transform:translateX(-100%);
          background:linear-gradient(90deg,transparent,rgba(255,255,255,.65),transparent);
          animation:shimmer 1.2s infinite;
        }
        @keyframes shimmer{100%{transform:translateX(100%)}}

        /* ✅ Skill dropdown */
        .skillDropWrap{position:relative; min-width:260px;}
        .skillDropBtn{
          width:260px;
          display:flex; align-items:center; justify-content:space-between;
          padding:6px 10px;
          border:1px solid rgba(0,0,0,.15);
          border-radius:6px;
          background:#fff;
          font-size:13px;
        }
        .skillDropMenu{
          position:absolute; top:100%; left:0; z-index:50;
          width:260px;
          background:#fff;
          border:1px solid rgba(0,0,0,.15);
          border-radius:10px;
          margin-top:6px;
          box-shadow:0 10px 30px rgba(0,0,0,.12);
          overflow:hidden;
        }
        .skillDropHead{padding:10px; border-bottom:1px solid rgba(0,0,0,.08);}
        .skillSearch{
          width:100%;
          border:1px solid rgba(0,0,0,.15);
          border-radius:8px;
          padding:7px 10px;
          font-size:13px;
          outline:none;
        }
        .skillActions{
          display:flex; padding:10px;
          border-bottom:1px solid rgba(0,0,0,.08);
          justify-content:space-between;
        }
        .skillList{max-height:240px; overflow:auto; padding:8px 5px;}
        .skillItem{display:flex; align-items:center; gap:10px; padding:6px 2px; font-size:13px;}
        .skillFoot{
          display:flex; gap:8px; justify-content:space-between;
          padding:10px;
          border-top:1px solid rgba(0,0,0,.08);
          background:#fafafa;
        }
      `}</style>

            <ContentWrapper>
                <div className="row">
                    <div className="col-12">
                        <ReportNavBar />
                        <div className="d-flex justify-content-between">
                            <div className="mt-2">
                                <h5 className="livecallcount mb-0">
                                    {rangeTitle}{" "}
                                    {isLoading ? (
                                        <>
                                            {" | "}
                                            <span
                                                style={{
                                                    display: "inline-block",
                                                    verticalAlign: "middle",
                                                    width: 420,
                                                }}
                                            >
                                                <SkeletonLine w="100%" h={14} mb={0} />
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            | Calls: {fmt(top.tCalls)} | Orders: {fmt(top.tOrders)} |
                                            Spend: ₹{fmtMoney(top.tSpend)} | Perf:{" "}
                                            {Number(top.tPerf || 0).toFixed(1)}%
                                        </>
                                    )}
                                </h5>

                                {isPendingChange && !isLoading && (
                                    <div className="pending">
                                        Range changed (not applied). Click Apply to load {startHour}–
                                        {endHour}.
                                    </div>
                                )}
                            </div>

                            {/* ✅ FILTERS */}
                            <div className="filterRow">
                                <div className="filterBox">
                                    <label>From Hour</label>
                                    <select
                                        className="form-select form-select-sm"
                                        value={startHour}
                                        onChange={(e) => setStartHour(Number(e.target.value))}
                                        disabled={isLoading}
                                    >
                                        {HOURS.map((h) => (
                                            <option key={h} value={h}>
                                                {h}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="filterBox">
                                    <label>To Hour</label>
                                    <select
                                        className="form-select form-select-sm"
                                        value={endHour}
                                        onChange={(e) => setEndHour(Number(e.target.value))}
                                        disabled={isLoading}
                                    >
                                        {HOURS.map((h) => (
                                            <option key={h} value={h}>
                                                {h}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* ✅ Skill multi-select */}
                                <div className="filterBox" ref={skillRef}>
                                    <label>Process Name</label>
                                    <div className="skillDropWrap">
                                        <button
                                            type="button"
                                            className="skillDropBtn"
                                            onClick={() => setSkillOpen((s) => !s)}
                                            disabled={isLoading || !skillOptions.length}
                                        >
                                            <span style={{ fontWeight: 600 }}>{skillButtonText}</span>
                                            <span style={{ opacity: 0.7 }}>▾</span>
                                        </button>

                                        {skillOpen && (
                                            <div className="skillDropMenu">
                                                <div className="skillDropHead">
                                                    <input
                                                        className="skillSearch"
                                                        value={skillSearch}
                                                        onChange={(e) => setSkillSearch(e.target.value)}
                                                        placeholder="Search skill..."
                                                    />
                                                </div>

                                                <div className="skillActions">
                                                    <button
                                                        type="button"
                                                        className="btnselectall"
                                                        onClick={onSelectAllSkills}
                                                    >
                                                        Select All
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btnresetall"
                                                        onClick={onClearSkills}
                                                    >
                                                        Clear
                                                    </button>
                                                </div>

                                                <div className="skillList">
                                                    {filteredSkillOptions.length ? (
                                                        filteredSkillOptions.map((name) => (
                                                            <label key={name} className="skillItem">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedSkills.has(name)}
                                                                    onChange={() => toggleSkill(name)}
                                                                />
                                                                <span>{name}</span>
                                                            </label>
                                                        ))
                                                    ) : (
                                                        <div
                                                            className="text-muted"
                                                            style={{ fontSize: 13, padding: "8px 2px" }}
                                                        >
                                                            No skills found
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="skillFoot">
                                                    <button
                                                        type="button"
                                                        className="btnselectall"
                                                        onClick={onResetSkillsToAll}
                                                    >
                                                        Reset All
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btnresetall"
                                                        onClick={onApplySkills}
                                                    >
                                                        Apply Skills
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <button
                                    className="btnskillapply"
                                    onClick={onApply}
                                    disabled={isLoading}
                                >
                                    Apply
                                </button>

                                <button
                                    className="btnskill"
                                    onClick={() => setShowSkills((s) => !s)}
                                    disabled={isLoading}
                                >
                                    {showSkills ? "Hide Skill View" : "Show Skill View"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ✅ Hour table */}
                <div className="card mt-3 shadow-sm">
                    <div className="card-body p-2">
                        <div className="table-responsive">
                            <table className="table table-sm table-bordered mb-0 mini-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 90 }}>Hour</th>
                                        <th style={{ width: 120, textAlign: "right" }}>Calls</th>
                                        <th style={{ width: 120, textAlign: "right" }}>Orders</th>
                                        <th style={{ width: 140, textAlign: "right" }}>Spend</th>
                                        <th style={{ width: 140, textAlign: "right" }}>Per Call</th>
                                        <th style={{ width: 100, textAlign: "right" }}>Perf</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading ? (
                                        <HourlyTableSkeleton
                                            rows={Math.max(4, (hours?.length || 0) || 6)}
                                        />
                                    ) : hours?.length ? (
                                        hours.map((h, idx) => (
                                            <tr key={idx}>
                                                <td>{h.hour}</td>
                                                <td style={{ textAlign: "right" }}>{fmt(h.calls)}</td>
                                                <td style={{ textAlign: "right" }}>{fmt(h.orders)}</td>
                                                <td style={{ textAlign: "right" }}>
                                                    ₹{fmtMoney(h.spend)}
                                                </td>
                                                <td style={{ textAlign: "right" }}>
                                                    ₹{fmtMoney(perCallCost(h.spend, h.calls))}
                                                </td>
                                                <td style={{ textAlign: "right" }}>
                                                    {Number(h.perf || 0).toFixed(1)}%
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={6} className="text-center">
                                                No hourly data in selected range
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* ✅ Skill-wise hourly view */}
                {showSkills && (
                    <div className="row mt-3" style={{ marginBottom: "50px" }}>
                        {isLoading ? (
                            <>
                                {Array.from({ length: 6 }).map((_, i) => (
                                    <SkillCardSkeleton key={`csk-${i}`} />
                                ))}
                            </>
                        ) : (
                            visibleSkillRows.map((r, idx) => (
                                <div className="col-md-6 col-lg-4 mb-3" key={idx}>
                                    <div className="card shadow-sm">
                                        <div className="card-body p-2">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <div style={{ fontWeight: 700 }} className="groupname">
                                                    {r.skillname}
                                                </div>
                                                <div className="text-muted" style={{ fontSize: 12 }}>
                                                    Calls: {fmt(r.total_calls)} | Orders:{" "}
                                                    {fmt(r.total_orders)} | ₹{fmtMoney(r.total_spend)}
                                                </div>
                                            </div>

                                            <div
                                                className="table-responsive"
                                                style={{ maxHeight: 260, overflowY: "auto" }}
                                            >
                                                <table className="table table-sm table-bordered mb-0 mini-table">
                                                    <thead>
                                                        <tr>
                                                            <th style={{ width: 60, textAlign: "right" }}>
                                                                Hour
                                                            </th>
                                                            <th style={{ width: 80, textAlign: "right" }}>
                                                                Calls
                                                            </th>
                                                            <th style={{ width: 80, textAlign: "right" }}>
                                                                Orders
                                                            </th>
                                                            <th style={{ width: 110, textAlign: "right" }}>
                                                                Spend
                                                            </th>
                                                            <th style={{ width: 110, textAlign: "right" }}>
                                                                Per Call
                                                            </th>
                                                            <th style={{ width: 70, textAlign: "right" }}>
                                                                Perf
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {(r.hours || []).length ? (
                                                            (r.hours || []).map((h, i2) => (
                                                                <tr key={i2}>
                                                                    <td style={{ textAlign: "right" }}>
                                                                        {h.hour}
                                                                    </td>
                                                                    <td style={{ textAlign: "right" }}>
                                                                        {fmt(h.calls)}
                                                                    </td>
                                                                    <td style={{ textAlign: "right" }}>
                                                                        {fmt(h.orders)}
                                                                    </td>
                                                                    <td style={{ textAlign: "right" }}>
                                                                        ₹{fmtMoney(h.spend)}
                                                                    </td>
                                                                    <td style={{ textAlign: "right" }}>
                                                                        ₹{fmtMoney(perCallCost(h.spend, h.calls))}
                                                                    </td>
                                                                    <td style={{ textAlign: "right" }}>
                                                                        {Number(h.perf || 0).toFixed(1)}%
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        ) : (
                                                            <tr>
                                                                <td colSpan={6} className="text-center">
                                                                    No skill hourly data
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}

                        {!isLoading && (!visibleSkillRows || visibleSkillRows.length === 0) && (
                            <div className="col-12">
                                <div className="alert alert-info mb-0">
                                    No skill-wise hourly data for selected skills.
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </ContentWrapper>
        </div>
    );
};

export default withAuth(HourlyPPCYesterdayReport);
